/**
 * @fileoverview TipTap Variant Block Extension
 *
 * Variant blocks let a single template hold multiple sentence-level wordings
 * (e.g. single vs. multiple practitioners). The active option is chosen at
 * job creation (jobs.variantSelections) and resolved at render time by
 * resolveVariantBlocks() in html-utils.ts.
 *
 * Two nodes:
 *  - variantBlock: container, holds one variantOption per group option
 *  - variantOption: regular block content, one per option
 *
 * Serialised HTML round-trip:
 *   <div data-type="variant-block"
 *        data-group-slug="practitioner_count"
 *        data-snippet-refs='{"single":null,"multiple":"snip_abc"}'>
 *     <div data-variant-option="single">…</div>
 *     <div data-variant-option="multiple">…</div>
 *   </div>
 */

import { mergeAttributes, Node as TiptapNode } from "@tiptap/core";
import type { NodeViewRendererProps } from "@tiptap/core";
import { DOMParser as PMDOMParser, DOMSerializer } from "@tiptap/pm/model";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

import {
  buildSlots,
  missingOptionsForSlot,
  slotLabelForRefs,
} from "./variant-slots";
import { extractHtmlFromEditor, stripInlineHighlights } from "./html-utils";

/**
 * Reduce a chunk of editor / snippet HTML to comparable plain text so a
 * variant option's current content can be diffed against its default library
 * snippet (MOR-781 "Edited" chip). We compare TEXT, not markup: field
 * placeholders on both sides are collapsed to their canonical `{{key}}` token
 * (`extractHtmlFromEditor` turns editor chips back into tokens; snippet
 * content already stores them), highlights are stripped, remaining tags are
 * dropped, and whitespace is normalised. This ignores structural / formatting
 * noise from TipTap's serialisation round-trip — which would otherwise flag an
 * untouched block as edited — and keys the chip off genuine wording changes.
 */
function normalizeVariantText(html: string): string {
  const canonical = extractHtmlFromEditor(stripInlineHighlights(html || ""));
  return canonical
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variantBlock: {
      /** Insert a new variant block with one empty option per group option. */
      insertVariantBlock: (attrs: {
        groupSlug: string;
        groupName?: string;
        options: Array<{ value: string; label: string }>;
      }) => ReturnType;
    };
  }
}

export interface VariantSnippet {
  _id: string;
  name: string;
  contentHtml: string;
  groupSlug: string;
  optionValue: string;
  /** Slot (row) the snippet belongs to within its group. */
  slotLabel?: string | null;
}

export interface VariantBlockOptions {
  HTMLAttributes: Record<string, unknown>;
  /**
   * Function returning the current snippet library. Called lazily by each
   * NodeView so live updates from useQuery propagate without a reconfigure.
   * Return [] (or omit) when there is no snippet library.
   */
  getSnippets?: () => VariantSnippet[];
  /**
   * Function returning the current variant group definitions. Called lazily
   * so live updates propagate without reconfigure. The NodeView uses this to
   * show the correct option labels (and filter to only current options)
   * instead of deriving them from the saved HTML children.
   */
  getVariantGroups?: () => Array<{
    slug: string;
    name: string;
    options: Array<{ value: string; label: string }>;
  }>;
  /**
   * Function returning the current variant selections for the document.
   * Maps group slug → selected option value. Used to pick the correct
   * initial active tab when the editor opens (instead of defaulting to the
   * first option with content).
   */
  getVariantSelections?: () => Record<string, string>;
  /**
   * Called when the user picks an option in a variant block's option menu.
   * The dialog uses this to keep the Preview tab's selection in sync with
   * whatever the author last viewed in Edit.
   */
  onVariantChange?: (groupSlug: string, optionValue: string) => void;
  /**
   * Document-editing mode (per-document day-one editor) vs. template
   * authoring. In document mode (MOR-781) the block hides the slot picker —
   * the author edits the already-filled content directly — and shows an
   * "Edited" / "Custom" chip flagging blocks whose content diverges from
   * their default library snippet. Template authoring keeps the slot picker
   * (it's the only way to wire snippets onto a block) and shows no chip.
   */
  documentMode?: boolean;
}

/** Generate a stable, collision-resistant id for a variant block instance. */
function makeVbId(): string {
  return `vb_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export const VariantBlock = TiptapNode.create<VariantBlockOptions>({
  name: "variantBlock",
  group: "block",
  content: "variantOption+",

  addOptions() {
    return {
      HTMLAttributes: {},
      getSnippets: undefined,
      getVariantGroups: undefined,
      getVariantSelections: undefined,
      onVariantChange: undefined,
      documentMode: false,
    };
  },

  addAttributes() {
    return {
      groupSlug: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-group-slug"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-group-slug": attrs.groupSlug,
        }),
      },
      groupName: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-group-name"),
        renderHTML: (attrs: Record<string, unknown>) => {
          if (!attrs.groupName) return {};
          return { "data-group-name": attrs.groupName };
        },
      },
      snippetRefs: {
        default: null,
        parseHTML: (el: HTMLElement) => {
          const raw = el.getAttribute("data-snippet-refs");
          if (!raw) return null;
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        },
        renderHTML: (attrs: Record<string, unknown>) => {
          if (!attrs.snippetRefs) return {};
          return { "data-snippet-refs": JSON.stringify(attrs.snippetRefs) };
        },
      },
      // Stable per-block id. Lets the day-one picker target an individual
      // block (a group can appear more than once in a doc) when overriding
      // its snippet. Generated on insert; backfilled for legacy blocks on
      // NodeView mount so existing precedents gain ids on next edit.
      vbId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-vb-id"),
        renderHTML: (attrs: Record<string, unknown>) => {
          if (!attrs.vbId) return {};
          return { "data-vb-id": attrs.vbId };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="variant-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "variant-block",
        class: "variant-block",
      }),
      0,
    ];
  },

  addNodeView() {
    // Capture extension options here — inside the returned NodeView
    // constructor `this` is not the extension instance, so we can't read
    // `this.options` once ProseMirror calls us back.
    const extensionOptions = this.options as VariantBlockOptions;
    return ({ node, editor, getPos }: NodeViewRendererProps) => {
      const wrapper = document.createElement("div");
      wrapper.classList.add("variant-block");
      wrapper.setAttribute("data-type", "variant-block");

      // Unique nonce so the per-block <style> rule below scopes only to
      // this NodeView's children. Survives PM rerenders because we set it
      // on `wrapper` (NodeView's own dom, never touched by PM).
      const nonce = `vb-${Math.random().toString(36).slice(2, 10)}`;
      wrapper.setAttribute("data-vb-nonce", nonce);

      const groupSlug = (node.attrs.groupSlug as string) ?? "";
      const groupName =
        (node.attrs.groupName as string) ||
        (groupSlug ? formatSlugAsLabel(groupSlug) : "Variant");
      if (groupSlug) wrapper.setAttribute("data-group-slug", groupSlug);

      // Backfill a stable id for legacy blocks (created before vbId existed)
      // so day-one per-block overrides have something to key on. Deferred to a
      // microtask to avoid dispatching a transaction inside the NodeView
      // constructor.
      const existingVbId = (node.attrs.vbId as string) ?? null;
      if (existingVbId) wrapper.setAttribute("data-vb-id", existingVbId);
      else if (typeof getPos === "function") {
        const id = makeVbId();
        wrapper.setAttribute("data-vb-id", id);
        queueMicrotask(() => {
          const pos = getPos();
          if (typeof pos !== "number") return;
          const { state, view } = editor;
          const n = state.doc.nodeAt(pos);
          if (n && n.type.name === "variantBlock" && !n.attrs.vbId) {
            view.dispatch(
              state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, vbId: id }),
            );
          }
        });
      }

      const tabsBar = document.createElement("div");
      tabsBar.classList.add("variant-block__tabs");
      tabsBar.setAttribute("contenteditable", "false");

      // Scoped style element. Lives inside tabsBar so its mutations are
      // already ignored by the tabsBar.contains() branch in ignoreMutation.
      // CSS-driven visibility means PM rebuilding the option DOM cannot
      // break the active-tab display — there's no per-render JS toggle to
      // get wiped.
      const styleEl = document.createElement("style");
      tabsBar.appendChild(styleEl);

      const groupLabel = document.createElement("span");
      groupLabel.classList.add("variant-block__group-label");
      groupLabel.textContent = groupName;
      tabsBar.appendChild(groupLabel);

      // Document mode (day-one per-document editor) hides the slot picker;
      // the precedent-library editor (template mode) keeps it. Both surface
      // the "Edited" / "Custom" chip (MOR-781).
      const documentMode = extensionOptions.documentMode === true;

      // "Edited" / "Custom" chip. Lives in the tab bar (ignored by PM's
      // mutation observer) so writing to it never bounces back into the
      // editor. Hidden until refreshEditedChip decides otherwise. Shown in
      // both modes.
      const editedChip = document.createElement("span");
      editedChip.classList.add("variant-block__edited-chip");
      editedChip.setAttribute("contenteditable", "false");
      editedChip.style.display = "none";
      editedChip.style.marginLeft = "8px";
      editedChip.style.padding = "1px 7px";
      editedChip.style.borderRadius = "9999px";
      editedChip.style.fontSize = "10px";
      editedChip.style.fontWeight = "600";
      editedChip.style.textTransform = "uppercase";
      editedChip.style.letterSpacing = "0.04em";
      tabsBar.appendChild(editedChip);

      const optionsContainer = document.createElement("div");
      optionsContainer.classList.add("variant-block__options");

      // Build option values from actual HTML children (so CSS visibility and
      // content replacement always target the right DOM element). Use the live
      // variant group definitions for labels — mapped by POSITION so stale
      // HTML values still get the correct friendly label.
      const getVariantGroups = extensionOptions.getVariantGroups;
      const liveGroups =
        typeof getVariantGroups === "function" ? getVariantGroups() : [];
      const liveGroup = liveGroups.find((g) => g.slug === groupSlug);

      const htmlChildValues: string[] = [];
      node.forEach((child) => {
        if (child.type.name === "variantOption") {
          const rawValue = child.attrs.value;
          htmlChildValues.push(
            typeof rawValue === "string" && rawValue.length > 0
              ? rawValue
              : `option_${htmlChildValues.length + 1}`,
          );
        }
      });

      const optionValues: string[] = [];
      const optionLabels = new Map<string, string>();

      if (liveGroup) {
        const count = Math.min(
          htmlChildValues.length,
          liveGroup.options.length,
        );
        for (let i = 0; i < count; i++) {
          optionValues.push(htmlChildValues[i]);
          optionLabels.set(htmlChildValues[i], liveGroup.options[i].label);
        }
      } else {
        for (const v of htmlChildValues) {
          optionValues.push(v);
          optionLabels.set(v, formatSlugAsLabel(v));
        }
      }

      const cssEscape = (s: string) => s.replace(/(["\\])/g, "\\$1");

      // Show EVERY option's content stacked, each prefixed with its option
      // label, so the author sees exactly what each option produces (e.g. both
      // "Greeting — Mum" and "Greeting — Dad"). The option is chosen later at
      // job time; the precedent shows them all.
      //
      // Driven entirely by a scoped <style> element (inside tabsBar, ignored
      // by PM's mutation observer). CSS attaches by data-variant-option, so it
      // survives PM re-rendering the option divs — we never touch the children.
      const renderOptionLabels = () => {
        const root = `.variant-block[data-vb-nonce="${nonce}"] > .variant-block__options`;
        const css = optionValues
          .map((value) => {
            const label = optionLabels.get(value) ?? value;
            const sel = `${root} > .variant-block__option[data-variant-option="${cssEscape(value)}"]`;
            return (
              `${sel} { display: block !important; position: relative; padding-top: 20px; margin-top: 8px; border-top: 1px dashed rgba(99,102,241,0.25); }` +
              // First option needs no separator / extra top gap.
              `${root} > .variant-block__option[data-variant-option="${cssEscape(value)}"]:first-child { margin-top: 0; border-top: none; }` +
              `${sel}::before { content: "${cssEscape(label)}"; position: absolute; top: 2px; left: 0; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6366f1; }`
            );
          })
          .join("");
        if (styleEl.textContent !== css) styleEl.textContent = css;
      };

      // Snippet picker — custom dropdown so it matches the variant block's
      // visual language (the native <select> takes the OS theme, which
      // looks completely out of place inside a styled editor).
      const snippetPicker = document.createElement("div");
      snippetPicker.classList.add("variant-block__snippet-picker");
      snippetPicker.setAttribute("contenteditable", "false");

      const snippetTrigger = document.createElement("button");
      snippetTrigger.type = "button";
      snippetTrigger.classList.add("variant-block__snippet-trigger");
      snippetTrigger.setAttribute("contenteditable", "false");

      const snippetTriggerLabel = document.createElement("span");
      snippetTriggerLabel.classList.add("variant-block__snippet-trigger-label");
      snippetTriggerLabel.textContent = "Insert variant";
      snippetTrigger.appendChild(snippetTriggerLabel);

      const snippetTriggerCaret = document.createElement("span");
      snippetTriggerCaret.classList.add("variant-block__snippet-trigger-caret");
      snippetTriggerCaret.textContent = "▾";
      snippetTrigger.appendChild(snippetTriggerCaret);

      const snippetMenu = document.createElement("div");
      snippetMenu.classList.add("variant-block__snippet-menu");
      snippetMenu.setAttribute("contenteditable", "false");
      snippetMenu.setAttribute("role", "listbox");

      snippetPicker.appendChild(snippetTrigger);
      snippetPicker.appendChild(snippetMenu);

      // Inline warning shown when the picked slot has no snippet for one or
      // more options (the author should add the missing-named snippet to the
      // library, or rename so the slot links across options).
      const slotWarning = document.createElement("span");
      slotWarning.classList.add("variant-block__slot-warning");
      slotWarning.setAttribute("contenteditable", "false");
      slotWarning.style.color = "#b45309"; // amber-700
      slotWarning.style.fontSize = "11px";
      slotWarning.style.marginLeft = "8px";

      let menuOpen = false;
      const setMenuOpen = (open: boolean) => {
        menuOpen = open;
        snippetPicker.classList.toggle("is-open", open);
        snippetTrigger.setAttribute("aria-expanded", String(open));
      };

      // Click-outside handler — close the menu when the user clicks
      // anywhere outside the picker.
      const onDocumentMouseDown = (e: MouseEvent) => {
        if (!menuOpen) return;
        if (!(e.target instanceof Node)) return;
        if (snippetPicker.contains(e.target)) return;
        setMenuOpen(false);
      };
      document.addEventListener("mousedown", onDocumentMouseDown);

      snippetTrigger.addEventListener("click", (e) => {
        e.stopPropagation();
        if (snippetTrigger.hasAttribute("data-disabled")) return;
        setMenuOpen(!menuOpen);
      });

      // The slot ("variant") this block currently references — the slotLabel
      // of the snippet pointed at by snippetRefs. Drives the trigger label and
      // the "already selected" state.
      const currentSlotName = (): string | null => {
        const getSnippets = extensionOptions.getSnippets;
        const snippets = typeof getSnippets === "function" ? getSnippets() : [];
        const rawRefs = wrapper.getAttribute("data-snippet-refs");
        if (!rawRefs) return null;
        try {
          const refs = JSON.parse(rawRefs) as Record<string, string | null>;
          return slotLabelForRefs(snippets, refs) ?? null;
        } catch {
          return null;
        }
      };

      // Persist the chosen slot's per-option snippet refs onto the node so
      // render (fillVariantSnippetPlaceholders) and the day-one preview can
      // resolve the slot. update() mirrors snippetRefs → data-snippet-refs.
      const setSnippetRefs = (refs: Record<string, string>) => {
        if (!editor) return;
        const pos = typeof getPos === "function" ? getPos() : null;
        if (typeof pos !== "number") return;
        const n = editor.state.doc.nodeAt(pos);
        if (!n || n.type.name !== "variantBlock") return;
        editor.view.dispatch(
          editor.state.tr.setNodeMarkup(pos, undefined, {
            ...n.attrs,
            snippetRefs: { ...(n.attrs.snippetRefs ?? {}), ...refs },
          }),
        );
      };

      const renderMissingWarning = (missing: string[]) => {
        if (missing.length === 0) {
          slotWarning.textContent = "";
          return;
        }
        const labels = missing.map((v) => optionLabels.get(v) ?? v).join(", ");
        slotWarning.textContent = `No snippet for: ${labels}`;
      };

      // Slot picker — lists the slots (rows) defined for this group. Picking a
      // slot fills EVERY option from the library in one action (the option
      // itself is chosen later, at job time).
      const refreshSlotOptions = () => {
        const getSnippets = extensionOptions.getSnippets;
        const snippets = typeof getSnippets === "function" ? getSnippets() : [];
        const slots = buildSlots(snippets, groupSlug, optionValues);
        snippetMenu.innerHTML = "";
        if (slots.length === 0) {
          snippetTrigger.setAttribute("data-disabled", "true");
          snippetTriggerLabel.textContent = "No variants";
          snippetTrigger.classList.remove("has-selection");
          const empty = document.createElement("div");
          empty.classList.add("variant-block__snippet-menu-empty");
          empty.textContent = "No variants defined for this group";
          snippetMenu.appendChild(empty);
          setMenuOpen(false);
          return;
        }
        snippetTrigger.removeAttribute("data-disabled");

        const current = currentSlotName();
        if (current) {
          snippetTriggerLabel.textContent = current;
          snippetTrigger.classList.add("has-selection");
        } else {
          // Default to picking the first variant rather than sitting on an
          // empty "Select variant" state (MOR-781). The render pipeline also
          // defaults an unselected block to the first library wording, so the
          // label matches what actually renders. The user can still switch via
          // the dropdown, and the "Edited" chip flags any divergence.
          snippetTriggerLabel.textContent = slots[0].label;
          snippetTrigger.classList.add("has-selection");
        }

        for (const slot of slots) {
          const item = document.createElement("button");
          item.type = "button";
          item.classList.add("variant-block__snippet-item");
          item.setAttribute("role", "option");
          item.textContent = slot.label;
          item.addEventListener("click", (e) => {
            e.stopPropagation();
            setMenuOpen(false);
            const refs = slot.refs;
            const missingOptions = missingOptionsForSlot(slot, optionValues);
            // Persist refs, then bake EVERY filled option's content so all
            // options show their version side by side.
            setSnippetRefs(refs);
            for (const opt of optionValues) {
              const sid = refs[opt];
              if (!sid) continue;
              const snip = snippets.find((s) => s._id === sid);
              if (snip) replaceActiveOptionContent(snip, opt);
            }
            snippetTriggerLabel.textContent = slot.label;
            snippetTrigger.classList.add("has-selection");
            renderMissingWarning(missingOptions);
            refreshDisplay();
          });
          snippetMenu.appendChild(item);
        }
      };

      const replaceActiveOptionContent = (
        snippet: VariantSnippet,
        activeValue: string,
      ) => {
        if (!editor) return;
        if (!activeValue) return;
        const blockPos = typeof getPos === "function" ? getPos() : null;
        if (typeof blockPos !== "number") return;
        const blockNode = editor.state.doc.nodeAt(blockPos);
        if (!blockNode || blockNode.type.name !== "variantBlock") {
          console.warn(
            "variant snippet insert: blockPos doesn't point at variantBlock",
            {
              blockPos,
              actualType: blockNode?.type.name,
            },
          );
          return;
        }

        // Walk variantBlock's descendants and find the variantOption whose
        // `value` matches the active tab. Using descendants() lets PM
        // compute absolute positions for us, so we don't have to maintain
        // an accumulator that could drift if a child has extra wrappers.
        const matches: Array<{
          pos: number;
          nodeSize: number;
          value: string;
        }> = [];
        const childSummary: Array<{ value: unknown; type: string }> = [];
        editor.state.doc.nodesBetween(
          blockPos + 1,
          blockPos + blockNode.nodeSize - 1,
          (node, pos) => {
            if (node.type.name === "variantBlock") return true; // descend
            if (node.type.name !== "variantOption") return false;
            childSummary.push({
              value: node.attrs.value,
              type: node.type.name,
            });
            if (node.attrs.value === activeValue) {
              matches.push({
                pos,
                nodeSize: node.nodeSize,
                value: node.attrs.value as string,
              });
            }
            return false; // don't descend into option's content
          },
        );
        // First match wins (mirrors the original guarded-assignment scan).
        const firstMatch = matches[0];
        const optionPos = firstMatch?.pos ?? null;
        const optionNodeSize = firstMatch?.nodeSize ?? null;
        const matchedValue = firstMatch?.value ?? null;

        if (optionPos === null || optionNodeSize === null) {
          console.warn("variant snippet insert: no matching variantOption", {
            activeValue,
            children: childSummary,
          });
          return;
        }
        const optionContentStart = optionPos + 1;
        const optionContentEnd = optionPos + optionNodeSize - 1;

        // Wrap the snippet HTML in <p> if it has no block-level wrapper —
        // variantOption requires `block+` content, so an empty / inline-only
        // snippet would crash the editor on the next render pass.
        const trimmed = (snippet.contentHtml ?? "").trim();
        const looksLikeBlockHtml =
          /^<(p|div|h[1-6]|ul|ol|blockquote|pre|table|hr)\b/i.test(trimmed);
        const safeHtml = trimmed
          ? looksLikeBlockHtml
            ? trimmed
            : `<p>${trimmed}</p>`
          : "<p></p>";

        try {
          editor
            .chain()
            .insertContentAt(
              { from: optionContentStart, to: optionContentEnd },
              safeHtml,
            )
            .setTextSelection(optionContentStart + 1)
            .focus()
            .run();
        } catch (err) {
          console.error(
            "variant snippet insert failed; falling back to plain text",
            err,
            { matchedValue, optionContentStart, optionContentEnd },
          );
          editor
            .chain()
            .insertContentAt(
              { from: optionContentStart, to: optionContentEnd },
              [
                {
                  type: "paragraph",
                  content: trimmed
                    ? [{ type: "text", text: trimmed.replace(/<[^>]+>/g, " ") }]
                    : [],
                },
              ],
            )
            .focus()
            .run();
        }
      };

      // Re-render the per-option labels and refresh the slot picker. Called
      // after a slot pick re-bakes content.
      const refreshDisplay = () => {
        renderOptionLabels();
        try {
          refreshSlotOptions();
        } catch {
          /* non-fatal */
        }
      };

      // Compare each option's current content against its default library
      // snippet and flag the block (MOR-781), in both modes:
      //  - no option has a library default  → "Custom"
      //  - some option diverges from its default → "Edited"
      //  - otherwise → no chip.
      // Default snippet per option mirrors fillVariantSnippetPlaceholders:
      // the block's snippetRefs pick wins, else the first library snippet for
      // (groupSlug, optionValue).
      const refreshEditedChip = (currentNode: ProseMirrorNode) => {
        try {
          const getSnippets = extensionOptions.getSnippets;
          const snippets =
            typeof getSnippets === "function" ? getSnippets() : [];
          const refs = (currentNode.attrs.snippetRefs ?? {}) as Record<
            string,
            string | null
          >;

          // Option child nodes in document order — aligned by index with
          // optionValues (built the same way from the HTML children).
          const optionNodes: ProseMirrorNode[] = [];
          currentNode.forEach((child) => {
            if (child.type.name === "variantOption") optionNodes.push(child);
          });

          const serializer = DOMSerializer.fromSchema(editor.schema);
          // Per-option status (has a default snippet? was it edited from
          // it?), derived independently so anyDefault/anyEdited fall out of
          // a plain `.some()` instead of an accumulator loop.
          const optionStatuses = optionValues.map((optValue, i) => {
            const optionNode = optionNodes[i];
            if (!optionNode) return { hasDefault: false, edited: false };
            const refId = refs[optValue];
            const def =
              (refId ? snippets.find((s) => s._id === refId) : undefined) ??
              snippets.find(
                (s) => s.groupSlug === groupSlug && s.optionValue === optValue,
              );
            if (!def) return { hasDefault: false, edited: false };
            const holder = document.createElement("div");
            holder.appendChild(
              serializer.serializeFragment(optionNode.content),
            );
            const currentText = normalizeVariantText(holder.innerHTML);
            // An unfilled option (empty, or the "Edit this for X" seed) is not
            // "edited" — it just hasn't been filled yet. Skip it so a freshly
            // inserted / never-touched block never reads as Edited (MOR-781).
            const unfilled =
              currentText === "" || currentText.startsWith("Edit this for");
            const edited =
              !unfilled &&
              currentText !== normalizeVariantText(def.contentHtml ?? "");
            return { hasDefault: true, edited };
          });
          const anyDefault = optionStatuses.some((s) => s.hasDefault);
          const anyEdited = optionStatuses.some((s) => s.edited);

          const chipStyle = !anyDefault
            ? { label: "Custom", bg: "rgba(100,116,139,0.15)", fg: "#475569" } // slate-600
            : anyEdited
              ? {
                  label: "Edited",
                  bg: "rgba(217,119,6,0.15)",
                  fg: "#b45309", // amber-700
                }
              : null;
          if (chipStyle) {
            editedChip.textContent = chipStyle.label;
            editedChip.style.background = chipStyle.bg;
            editedChip.style.color = chipStyle.fg;
            editedChip.style.display = "inline-block";
          } else {
            editedChip.style.display = "none";
          }
        } catch {
          /* non-fatal — never let the chip break editing */
        }
      };

      // The slot picker is the only control in the precedent — you pick the
      // variant (row); the option (column) is chosen later at job time. There
      // is deliberately NO option/preview toggle here (it confused authors and
      // implied a choice that doesn't belong in the precedent).
      //
      // Document mode (day-one per-document editor) hides the picker entirely
      // (MOR-781): the content is already filled from the library, so the
      // author just edits it inline; picking a named slot there read as
      // "tweak this one variant" rather than "edit the shared content".
      if (!documentMode) {
        tabsBar.appendChild(snippetPicker);
        tabsBar.appendChild(slotWarning);
      }

      // Delete button — removes the entire variant block from the document.
      const deleteBtn = document.createElement("button");
      deleteBtn.type = "button";
      deleteBtn.classList.add("variant-block__delete");
      deleteBtn.setAttribute("contenteditable", "false");
      deleteBtn.setAttribute("title", "Remove variant block");
      deleteBtn.innerHTML = "×";
      deleteBtn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!editor) return;
        const blockPos = typeof getPos === "function" ? getPos() : null;
        if (typeof blockPos !== "number") return;
        const blockNode = editor.state.doc.nodeAt(blockPos);
        if (!blockNode || blockNode.type.name !== "variantBlock") return;
        editor
          .chain()
          .focus()
          .deleteRange({
            from: blockPos,
            to: blockPos + blockNode.nodeSize,
          })
          .run();
      });
      tabsBar.appendChild(deleteBtn);

      // Default the settings Preview tab to the first option (the editor shows
      // all options, but the read-only Preview resolves to a single one).
      const getVariantSelections = extensionOptions.getVariantSelections;
      const selections =
        typeof getVariantSelections === "function"
          ? getVariantSelections()
          : {};
      const selectedValue = groupSlug ? selections[groupSlug] : undefined;
      const defaultOption =
        selectedValue && optionValues.includes(selectedValue)
          ? selectedValue
          : (optionValues[0] ?? "");
      if (
        groupSlug &&
        defaultOption &&
        selectedValue !== defaultOption &&
        extensionOptions.onVariantChange
      ) {
        extensionOptions.onVariantChange(groupSlug, defaultOption);
      }

      renderOptionLabels();
      try {
        refreshSlotOptions();
      } catch {
        /* non-fatal */
      }

      wrapper.appendChild(tabsBar);
      wrapper.appendChild(optionsContainer);

      refreshEditedChip(node);

      tabsBar.addEventListener("click", (e) => e.stopPropagation());

      // Re-apply the option labels once TipTap has actually inserted child
      // option <div>s into our contentDOM. Microtask handles the common case;
      // requestAnimationFrame is a backstop in case PM mounts children on the
      // next frame instead of synchronously.
      queueMicrotask(renderOptionLabels);
      requestAnimationFrame(renderOptionLabels);

      return {
        dom: wrapper,
        contentDOM: optionsContainer,

        /**
         * Tell ProseMirror to leave events inside the tab strip alone — it
         * contains a native `<select>` and `<button>`s whose events PM
         * would otherwise intercept (treating them as clicks inside the
         * editor). Without this, change/click events on the snippet picker
         * never reach our handlers.
         */
        stopEvent(event) {
          return tabsBar.contains(event.target as Node);
        },

        /**
         * Ignore mutations that aren't real document edits — without this,
         * the visibility toggle bounces back into ProseMirror's observer,
         * triggering update → applyActive → mutation → update → ... and
         * the tab process eventually OOMs.
         *
         * Specifically ignore:
         *  - everything in the tab strip (outside contentDOM anyway)
         *  - our `data-active-option` attribute flip on the wrapper itself
         *  - class / inline-style flips on the immediate children of
         *    contentDOM (the visibility toggle that controls which option
         *    is shown)
         */
        ignoreMutation(mutation) {
          if (tabsBar.contains(mutation.target as Node)) return true;
          if (mutation.type === "attributes" && mutation.target === wrapper) {
            return true;
          }
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "class" ||
              mutation.attributeName === "style") &&
            mutation.target instanceof HTMLElement &&
            mutation.target.parentElement === optionsContainer
          ) {
            return true;
          }
          return false;
        },

        update(updatedNode) {
          if (updatedNode.type.name !== "variantBlock") return false;
          const newRefs = updatedNode.attrs.snippetRefs as Record<
            string,
            string | null
          > | null;
          const serialised = newRefs ? JSON.stringify(newRefs) : null;
          const existing = wrapper.getAttribute("data-snippet-refs");
          if (serialised && existing !== serialised) {
            wrapper.setAttribute("data-snippet-refs", serialised);
          } else if (!serialised && existing !== null) {
            wrapper.removeAttribute("data-snippet-refs");
          }
          // Recompute the "Edited" / "Custom" chip against the updated content
          // (document mode only; no-op otherwise).
          refreshEditedChip(updatedNode);
          // Visibility is driven by the scoped <style> element written by
          // applyActive — PM regenerating the variantOption divs cannot
          // break it (CSS attaches by data-variant-option attribute,
          // which round-trips through PM). So no re-apply needed here.
          return true;
        },
        destroy() {
          document.removeEventListener("mousedown", onDocumentMouseDown);
        },
      };
    };
  },

  addCommands() {
    return {
      insertVariantBlock:
        ({ groupSlug, groupName, options }) =>
        ({ commands, state }) => {
          if (options.length === 0) return false;

          // Default a freshly-inserted block to the first library variant
          // ("slot") so it shows real wording straight away instead of the
          // "Edit this for X" placeholder — and the "Edited" chip doesn't fire
          // on an untouched default (MOR-781). Falls back to the placeholder
          // per option when the group (or that option) has no library snippet.
          const getSnippets = this.options.getSnippets;
          const snippets =
            typeof getSnippets === "function" ? getSnippets() : [];
          const optionValues = options.map((o) => o.value);
          const defaultSlot = buildSlots(snippets, groupSlug, optionValues)[0];

          const pmParser = PMDOMParser.fromSchema(state.schema);
          const parseHtmlToContent = (html: string) => {
            const holder = document.createElement("div");
            const trimmed = (html ?? "").trim();
            holder.innerHTML = trimmed || "<p></p>";
            const parsed = pmParser.parse(holder).toJSON();
            const parsedContent = parsed.content;
            return Array.isArray(parsedContent) && parsedContent.length > 0
              ? parsedContent
              : [{ type: "paragraph" }];
          };

          // One variantOption per group option — seeded with the default
          // slot's snippet content when available, else the placeholder hint.
          const content = options.map((opt) => {
            const snippetId = defaultSlot?.refs[opt.value] ?? null;
            const snippet = snippetId
              ? snippets.find((s) => s._id === snippetId)
              : undefined;
            return {
              type: "variantOption",
              attrs: { value: opt.value, snippetId: null },
              content: snippet
                ? parseHtmlToContent(snippet.contentHtml)
                : [
                    {
                      type: "paragraph",
                      content: [
                        { type: "text", text: `Edit this for "${opt.label}"` },
                      ],
                    },
                  ],
            };
          });

          const variantBlockJson = {
            type: "variantBlock",
            attrs: {
              groupSlug,
              groupName: groupName ?? null,
              snippetRefs: defaultSlot ? defaultSlot.refs : null,
              vbId: makeVbId(),
            },
            content,
          };

          // Pick a safe top-level insertion position. Variant blocks are
          // block-level — inserting at the cursor inside a paragraph / list
          // item would be rejected, so we walk up to the doc's direct
          // children and insert AFTER the current top-level block.
          const rawInsertPos: number = (() => {
            try {
              const { $from } = state.selection;
              // $from.after(1) is the position right after the depth-1 node
              // (the top-level block containing the cursor). Falls through to
              // doc end if depth < 1 (rare — empty doc / no cursor).
              return $from.depth >= 1 ? $from.after(1) : state.doc.content.size;
            } catch {
              return state.doc.content.size;
            }
          })();
          // Clamp to a valid position within the doc.
          const insertPos = Math.max(
            0,
            Math.min(rawInsertPos, state.doc.content.size),
          );

          // commands.insertContentAt returns true on success. Folds into the
          // outer chain (e.g. the toolbar's `.chain().focus().insertVariantBlock(...).run()`)
          // as a single transaction — do NOT call .run() here.
          return commands.insertContentAt(insertPos, [
            variantBlockJson,
            { type: "paragraph" },
          ]);
        },
    };
  },
});

export const VariantOption = TiptapNode.create({
  name: "variantOption",
  // Live in the standard `block` group so ProseMirror's defaults play nicely
  // (paragraph/list inserts, splits, etc.). The variantBlock parent constrains
  // its children by node name (`variantOption+`), so this doesn't let the
  // option escape into the normal block flow.
  group: "block",
  content: "block+",

  addAttributes() {
    return {
      value: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-variant-option"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-variant-option": attrs.value,
        }),
      },
      snippetId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-snippet-id"),
        renderHTML: (attrs: Record<string, unknown>) => {
          if (!attrs.snippetId) return {};
          return { "data-snippet-id": attrs.snippetId };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-variant-option]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: "variant-block__option",
      }),
      0,
    ];
  },
});

function formatSlugAsLabel(slug: string): string {
  return slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
